import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity,
  Building2,
  CalendarClock,
  FileSignature,
  FileText,
  Handshake,
  Upload,
} from 'lucide-react';
import StatusBadge from '../../components/StatusBadge';
import { RequestStatusBadge, RiskRatingBadge } from '../../components/RequestBadges';
import { currencyOnFileNote, fmtCurrency, fmtDate, fmtMoney, relativeTime, resolveCurrencyCode } from '../../utils/format';
import { counterpartyPath } from './counterpartyApi';

// Counterparty 360 sections (doc-174). Every section DEEP-LINKS into the
// surface that owns the row — contracts to /contracts/:id, requests to
// /tprm/:id — so this page aggregates the relationship without becoming a
// second place to edit any of it.

const DATE_KIND_CHIP = {
  expiration: { className: 'chip sq yellow', label: 'Expires' },
  notice: { className: 'chip sq red', label: 'Notice' },
  obligation: { className: 'chip sq blue', label: 'Obligation' },
};

// A relationship can hold hundreds of contracts, and this page renders every
// section in full — so both tables cap their rows here (doc-191). One constant,
// both tables.
const SECTION_ROW_CAP = 25;

// House pattern for ANY truncated list from here on: a muted footer line at the
// end of the list reading "Showing N of TOTAL". The count chip in the section
// header keeps telling the truth about the whole relationship; this footer
// reconciles it with what is actually on screen. A bare slice under a full-count
// chip is the doc-145 / doc-158 / doc-170 family of numbers a reader cannot
// reconcile, so capping a list WITHOUT this footer is not an option.
//
// The 18px inset matches the paging footers on the Contracts and Activity
// tables; card-body lists pass 24 so the line hangs under their own rows.
function TruncationFooter({ inset = 18, children }) {
  return (
    <div
      className="row"
      style={{
        padding: `10px ${inset}px`,
        borderTop: '0.5px solid var(--border-primary)',
        gap: 10,
        fontSize: 12,
        color: 'var(--text-secondary)',
      }}
    >
      {children}
    </div>
  );
}

// Expansion is purely client-side: the whole array already arrived in the detail
// payload (the endpoint is deliberately unpaged — see doc-191), so "Show all"
// never refetches.
function useRowCap(rows) {
  const [expanded, setExpanded] = useState(false);
  const capped = rows.length > SECTION_ROW_CAP;
  return {
    visible: capped && !expanded ? rows.slice(0, SECTION_ROW_CAP) : rows,
    capped,
    expanded,
    toggle: () => setExpanded((value) => !value),
  };
}

function RowCapFooter({ shown, total, expanded, onToggle }) {
  return (
    <TruncationFooter>
      <span>{expanded ? `Showing all ${total}` : `Showing ${shown} of ${total}`}</span>
      <button type="button" className="btn ghost xs" onClick={onToggle}>
        {expanded ? 'Show fewer' : 'Show all'}
      </button>
    </TruncationFooter>
  );
}

export function EmptyBlock({ icon: Icon = FileText, title, hint }) {
  return (
    <div className="card-body" style={{ padding: '40px 24px', textAlign: 'center' }}>
      <div
        style={{
          width: 40, height: 40, borderRadius: 999,
          background: 'var(--surface-alpha-tertiary)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: 10,
        }}
      >
        <Icon className="w-4 h-4" style={{ color: 'var(--icon-tertiary)' }} />
      </div>
      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 3 }}>{title}</div>
      {hint && <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{hint}</div>}
    </div>
  );
}

function SectionCard({ title, subtitle, count, children }) {
  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header">
        <div>
          <div className="card-title">{title}</div>
          {subtitle && <div className="card-subtitle">{subtitle}</div>}
        </div>
        {count != null && <span className="chip gray tnum">{count}</span>}
      </div>
      {children}
    </div>
  );
}

// The fold is the one thing about this page a reader cannot verify by eye, so
// it is stated out loud: which spellings and which group landed here.
export function CounterpartyIdentity({ entity }) {
  const others = (entity.members || []).filter((m) => m.name !== entity.displayName);
  return (
    <div className="muted row" style={{ gap: 8, fontSize: 12.5, flexWrap: 'wrap' }}>
      <span className={`chip sq ${entity.kind === 'group' ? 'purple' : 'gray'}`}>
        {entity.kind === 'group' ? 'Counterparty group' : 'Counterparty'}
      </span>
      {others.length > 0 && (
        <span title={others.map((m) => m.name).join('\n')}>
          Also covers {others.slice(0, 3).map((m) => m.name).join(', ')}
          {others.length > 3 ? ` +${others.length - 3} more` : ''}
        </span>
      )}
    </div>
  );
}

// "$250K across 1 of 3 active contracts" — the denominator is every active
// contract, the numerator only the ones with a value on file. Collapses to the
// simple phrasing when they agree, and says so when NO active contract has a
// value (the tile then shows an em dash, not a zero).
//
// MIXED CURRENCIES (doc-188): when the valued active contracts span more than
// one currency the backend refuses to emit a total, because there is no FX
// source and summing USD+EUR produces a number that means nothing. The tile
// shows an em dash and this line says why, rather than printing a mixed sum
// under a dollar sign.
//
// UNRESOLVABLE CODE (doc-190): contracts.currency is free-form AI-extracted
// text, so the bucket code can be a string Intl cannot format ('US DOLLARS').
// The tile value then drops to a bare number, and — unlike the table cells that
// share this defect — this one appends what is on file: it is a standalone
// entity-page headline with horizontal room and no Currency field anywhere near
// it, so the raw string would otherwise be lost from the page entirely.
// The on-file clause is only reachable on the branch that renders "across N …"
// — every earlier branch in valueSub returns a different sentence entirely. One
// guard chain here so valueSub and valueSubTitle can never disagree about
// whether the note is showing (doc-221).
const activeValueNote = ({
  activeContracts = 0,
  valuedActiveContracts = 0,
  activeValueCurrencies = 0,
  activeValueCurrency = null,
}) => {
  if (activeContracts === 0 || activeValueCurrencies > 1 || valuedActiveContracts === 0) return null;
  return currencyOnFileNote(activeValueCurrency);
};

export function valueSub(totals) {
  const {
    activeContracts = 0,
    valuedActiveContracts = 0,
    activeValueCurrencies = 0,
  } = totals;
  const noun = activeContracts === 1 ? 'active contract' : 'active contracts';
  if (activeContracts === 0) return 'no active contracts';
  if (activeValueCurrencies > 1) return `values in ${activeValueCurrencies} currencies — not summed`;
  if (valuedActiveContracts === 0) return `no value on file for ${activeContracts} ${noun}`;
  const across = valuedActiveContracts === activeContracts
    ? `across ${activeContracts} ${noun}`
    : `across ${valuedActiveContracts} of ${activeContracts} ${noun}`;
  // currencyOnFileNote trims and clamps the raw string — the same helper the
  // Overview fact strip calls, so the two surfaces stay identical (doc-221).
  const note = activeValueNote(totals);
  return note ? `${across} · ${note.text}` : across;
}

// The full, unclamped phrase for the tile's title attribute — null unless the
// note actually got clamped, so an untruncated tile grows no tooltip.
export const valueSubTitle = (totals) => {
  const note = activeValueNote(totals);
  return (note && note.title) || null;
};

// USD keeps the compact "$250K" form; other codes get the full fmtCurrency
// form ("€500,000") so this headline tile spells the amount out. fmtMoney does
// honor its currency argument now (doc-187) — the split below is a deliberate
// compact-vs-full choice for this tile, not a workaround, so keep both branches.
// A code that resolves to nothing takes the full branch too and renders a bare
// "500,000" (doc-190); valueSub above names the on-file string underneath.
export function fmtActiveValue({ activeContractValue, activeValueCurrency }) {
  if (activeContractValue == null || activeContractValue === 0) return '—';
  const raw = activeValueCurrency || 'USD';
  if (resolveCurrencyCode(raw) === 'USD') return fmtMoney(activeContractValue);
  return fmtCurrency(activeContractValue, raw) || '—';
}

export function CounterpartyStats({ totals }) {
  const statusBits = Object.entries(totals.statusCounts || {})
    .filter(([, count]) => count > 0)
    .map(([status, count]) => `${count} ${status.replace(/_/g, ' ')}`)
    .join(' · ');

  const tiles = [
    {
      key: 'contracts',
      label: 'Contracts',
      value: totals.contracts,
      sub: statusBits || 'No contracts on file',
    },
    {
      key: 'value',
      label: 'Active value',
      value: fmtActiveValue(totals),
      // The number sums only the active contracts that carry a value AND share a
      // currency, so the sub says how many of the active contracts that is —
      // "across 3 active contracts" under a total drawn from one of them is the
      // unit mix doc-158 was filed for.
      sub: valueSub(totals),
      // Only set when the on-file currency string was clamped (doc-221).
      subTitle: valueSubTitle(totals),
    },
    {
      key: 'requests',
      label: 'TPRM requests',
      value: totals.requests,
      sub: totals.openRequests > 0
        ? `${totals.openRequests} still open`
        : 'none open',
    },
    {
      key: 'dates',
      label: 'Upcoming dates',
      value: totals.upcomingDates,
      // No obligations means no clause at all — "incl. 0 open obligations" is
      // true but clunky, and it was on most entities (doc-190).
      sub: totals.openObligations > 0
        ? `incl. ${totals.openObligations} open obligation${totals.openObligations === 1 ? '' : 's'}`
        : null,
    },
  ];

  return (
    <div className="stat-row" style={{ '--stat-cols': tiles.length, marginBottom: 24 }}>
      {tiles.map((tile) => (
        <div className="stat-card" key={tile.key}>
          <div className="stat-label">{tile.label}</div>
          <div className="stat-valrow">
            <div className="stat-value">{tile.value}</div>
          </div>
          {tile.sub && <div className="stat-sub" title={tile.subTitle || undefined}>{tile.sub}</div>}
        </div>
      ))}
    </div>
  );
}

export function CounterpartyContracts({ contracts, displayName }) {
  const { visible, capped, expanded, toggle } = useRowCap(contracts);
  return (
    <SectionCard
      title="Contracts"
      subtitle="Every agreement on this relationship, including terminated ones."
      // The header count stays the whole relationship; the footer says how much
      // of it is on screen.
      count={contracts.length}
    >
      {contracts.length === 0 && (
        <EmptyBlock
          icon={FileSignature}
          title="No contracts yet"
          hint={`Nothing is on file for ${displayName} — a TPRM request may still be in flight.`}
        />
      )}
      {contracts.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>Contract</th>
                <th style={{ width: 110 }}>Status</th>
                <th style={{ width: 110, textAlign: 'right' }}>Value</th>
                <th style={{ width: 120 }}>Effective</th>
                <th style={{ width: 120 }}>Expires</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((c) => (
                <tr key={c.id}>
                  <td>
                    <Link to={`/contracts/${c.id}`} style={{ fontSize: 13 }}>{c.title || '(untitled)'}</Link>
                    <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                      {[c.contractType, c.counterparty, c.internalOwner].filter(Boolean).join(' · ') || '—'}
                    </div>
                  </td>
                  <td><StatusBadge status={c.status} /></td>
                  <td className="mono" style={{ textAlign: 'right', fontSize: 12.5 }}>
                    {c.contractValue ? fmtMoney(c.contractValue, c.currency || 'USD') : '—'}
                  </td>
                  <td className="muted" style={{ fontSize: 12 }}>{fmtDate(c.effectiveDate)}</td>
                  <td className="muted" style={{ fontSize: 12 }}>{fmtDate(c.expirationDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {capped && (
        <RowCapFooter shown={visible.length} total={contracts.length} expanded={expanded} onToggle={toggle} />
      )}
    </SectionCard>
  );
}

export function CounterpartyRequests({ requests }) {
  const { visible, capped, expanded, toggle } = useRowCap(requests);
  return (
    <SectionCard
      title="TPRM requests"
      subtitle="Vendor onboarding and re-assessment requests matched to this counterparty."
      count={requests.length}
    >
      {requests.length === 0 && (
        <EmptyBlock icon={Handshake} title="No TPRM requests" hint="Nothing has been filed for this vendor." />
      )}
      {requests.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table className="tbl">
            <thead>
              <tr>
                <th>Vendor / requester</th>
                <th style={{ width: 130 }}>Status</th>
                <th style={{ width: 90 }}>Risk</th>
                <th style={{ width: 120 }}>Updated</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.id}>
                  <td>
                    <Link to={`/tprm/${r.id}`} style={{ fontSize: 13 }}>{r.vendorName || 'Untitled request'}</Link>
                    {r.archived && <span className="chip gray sq" style={{ marginLeft: 6 }}>Archived</span>}
                    <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                      Requested by {r.requesterName || '—'}
                      {r.contractId && (
                        <> · <Link to={`/contracts/${r.contractId}`}>contract created</Link></>
                      )}
                    </div>
                  </td>
                  <td><RequestStatusBadge status={r.status} /></td>
                  <td><RiskRatingBadge rating={r.riskRating} /></td>
                  <td className="muted" style={{ fontSize: 12 }}>{relativeTime(r.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {capped && (
        <RowCapFooter shown={visible.length} total={requests.length} expanded={expanded} onToggle={toggle} />
      )}
    </SectionCard>
  );
}

// The backend hands back at most 50 items alongside a truthful pre-slice total
// (counterparty-service.js buildUpcomingDates), so the chip could read 1200 over
// 50 rows with nothing saying so (doc-191). There is no expander here on purpose
// — the other 1150 are not in the payload, so the footer discloses the slice
// rather than pretending it can reveal it.
export function CounterpartyUpcoming({ items, total }) {
  const truncated = total > items.length;
  return (
    <div className="card">
      <div className="card-header">
        <div>
          <div className="card-title">Upcoming dates</div>
          <div className="card-subtitle">Expirations, renewal-notice deadlines and open obligations.</div>
        </div>
        {total > 0 && <span className="chip gray tnum">{total}</span>}
      </div>
      {items.length === 0 && (
        <EmptyBlock icon={CalendarClock} title="Nothing dated ahead" hint="No expirations, notices or obligations due." />
      )}
      {/* The footer supplies its own top border and padding, so the body drops
          its bottom padding when one is coming. */}
      {items.length > 0 && (
        <div className="card-body" style={{ paddingTop: 0, paddingBottom: truncated ? 0 : undefined }}>
          {items.map((item, index) => {
            const chip = DATE_KIND_CHIP[item.kind] || DATE_KIND_CHIP.obligation;
            return (
              <div
                key={`${item.kind}-${item.contractId}-${item.date}-${index}`}
                className="row"
                style={{
                  gap: 10,
                  padding: '8px 0',
                  borderBottom: index === items.length - 1 ? 0 : '0.5px solid var(--border-primary)',
                  alignItems: 'flex-start',
                }}
              >
                <span className={chip.className} style={{ flexShrink: 0 }}>{chip.label}</span>
                {/* An obligation's own title is the headline and the contract is
                    the subline; a contract date has only the contract, so the
                    headline IS the link and there is no subline to pad. */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, color: 'var(--text-primary)' }}>
                    {item.kind === 'obligation'
                      ? item.label
                      : <Link to={`/contracts/${item.contractId}`}>{item.contractTitle || '(untitled)'}</Link>}
                  </div>
                  {item.kind === 'obligation' && (
                    <div className="muted ellipsis" style={{ fontSize: 11, marginTop: 1 }}>
                      <Link to={`/contracts/${item.contractId}`}>{item.contractTitle || 'contract'}</Link>
                    </div>
                  )}
                </div>
                <div className="mono muted" style={{ fontSize: 11, flexShrink: 0 }}>{fmtDate(item.date)}</div>
              </div>
            );
          })}
        </div>
      )}
      {items.length > 0 && truncated && (
        <TruncationFooter inset={24}>
          <span>Showing the next {items.length} of {total}</span>
        </TruncationFooter>
      )}
    </div>
  );
}

// Purpose-built rather than shared with the dashboard feed: this page already
// knows the counterparty, so the row drops that subline and keeps the verb.
function ActivityLine({ event }) {
  let verb = 'touched';
  let Icon = FileText;
  if (event.kind === 'created') {
    verb = event.details?.isWizard ? 'generated' : 'created';
  } else if (event.kind === 'file_uploaded') {
    Icon = Upload;
    if (event.details?.direction === 'inbound') verb = 'uploaded a counterparty markup to';
    else if (event.details?.isExecuted) verb = 'uploaded the executed version of';
    else verb = `uploaded ${event.details?.draftVersion || 'a draft'} to`;
  } else if (event.kind === 'status_changed') {
    Icon = Activity;
    verb = 'changed status of';
  }
  const tail = event.kind === 'status_changed' && event.details?.newStatus
    ? ` → ${event.details.newStatus}`
    : '';

  return (
    <div className="row" style={{ gap: 8, padding: '8px 0', alignItems: 'flex-start' }}>
      <Icon className="w-3.5 h-3.5" style={{ color: 'var(--icon-tertiary)', flexShrink: 0, marginTop: 2 }} />
      <div style={{ flex: 1, minWidth: 0, fontSize: 12.5 }}>
        <strong style={{ fontWeight: 500 }}>{event.userName || event.userEmail || 'System'}</strong>{' '}
        <span className="muted">{verb}</span>{' '}
        <Link to={`/contracts/${event.contractId}`}>{event.contractTitle || '(untitled)'}</Link>
        {tail && <span className="muted mono" style={{ fontSize: 11 }}>{tail}</span>}
      </div>
      <div className="mono muted" style={{ fontSize: 11, flexShrink: 0 }}>{relativeTime(event.timestamp)}</div>
    </div>
  );
}

export function CounterpartyActivity({ events }) {
  return (
    <div className="card">
      <div className="card-header">
        <div className="card-title">Recent activity</div>
      </div>
      {events.length === 0 ? (
        <EmptyBlock icon={Activity} title="No activity yet" />
      ) : (
        <div className="card-body" style={{ paddingTop: 0 }}>
          {events.map((event, index) => (
            <ActivityLine key={`${event.kind}-${event.contractId}-${event.timestamp}-${index}`} event={event} />
          ))}
        </div>
      )}
    </div>
  );
}

// Row for the index page.
export function CounterpartyRow({ entry }) {
  return (
    <>
      <td>
        <Link to={counterpartyPath(entry.key)} style={{ fontSize: 13 }}>{entry.displayName}</Link>
        {entry.kind === 'group' && (
          <span className="chip purple sq" style={{ marginLeft: 6 }}>
            <Building2 className="w-[10px] h-[10px]" /> Group
          </span>
        )}
        {entry.names.length > 1 && (
          // Truncated on narrow columns, so the full fold is in the tooltip.
          <div
            className="muted ellipsis"
            style={{ fontSize: 11, marginTop: 2, maxWidth: 420 }}
            title={entry.names.join('\n')}
          >
            {entry.names.join(' · ')}
          </div>
        )}
      </td>
      <td className="tnum" style={{ fontSize: 12.5 }}>
        {entry.contractCount}
        {entry.activeContractCount > 0 && (
          <span className="muted" style={{ fontSize: 11 }}> ({entry.activeContractCount} active)</span>
        )}
      </td>
      {/* The count and the open-requests chip need real space between them: as
          adjacent inline boxes "1" + "1 open" reads as the number eleven. */}
      <td style={{ fontSize: 12.5 }}>
        <span className="row" style={{ gap: 8 }}>
          <span className="tnum">{entry.requestCount}</span>
          {entry.openRequestCount > 0 && (
            <span className="chip yellow sq">{entry.openRequestCount} open</span>
          )}
        </span>
      </td>
      <td className="muted" style={{ fontSize: 12 }}>{relativeTime(entry.lastActivityAt)}</td>
    </>
  );
}
